import { useState, useRef, useCallback, useEffect } from 'react';
import api from '@/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Hook: Drawing extraction SSE progress with DB polling fallback.
 * Encapsulates SSE connection lifecycle + polling for resilience.
 *
 * Returns:
 *  - drawingProgress: current progress object from SSE/poll
 *  - drawingExtracting: boolean, true while extraction is in progress
 *  - completionResult: { status: 'done'|'error', data } when extraction finishes
 *  - startTracking(): call to begin listening for SSE events
 *  - reset(): clear all state
 */
export function useDrawingExtractionProgress(projectId) {
  const [drawingProgress, setDrawingProgress] = useState(null);
  const [drawingExtracting, setDrawingExtracting] = useState(false);
  const [completionResult, setCompletionResult] = useState(null);
  const esRef = useRef(null);

  useEffect(() => {
    if (!drawingExtracting) return;
    const token = localStorage.getItem('fmiq_token');
    if (!token) return;

    const url = `${API_URL}/api/closeout/projects/${projectId}/events/stream?token=${encodeURIComponent(token)}`;
    let es;
    try { es = new EventSource(url); } catch { return; }
    esRef.current = es;

    let lastSseTime = Date.now();

    es.addEventListener('drawing_extraction_progress', (e) => {
      lastSseTime = Date.now();
      try { setDrawingProgress(JSON.parse(e.data)); } catch {}
    });

    es.addEventListener('drawing_extraction_complete', (e) => {
      lastSseTime = Date.now();
      try {
        const data = JSON.parse(e.data);
        setDrawingProgress({ ...data, phase: data.phase || 'complete' });
        setDrawingExtracting(false);
        if (data.status === 'done') {
          setCompletionResult({ status: 'done', data });
        } else if (data.status === 'error') {
          setCompletionResult({ status: 'error', data });
        }
      } catch {}
    });

    es.onerror = () => {};

    // Polling fallback: if SSE stalls for >12s, poll DB-persisted progress
    const pollInterval = setInterval(async () => {
      if (Date.now() - lastSseTime < 12000) return;
      try {
        const res = await api.get(`/closeout/projects/${projectId}/extraction-status`);
        const data = res.data;
        if (!data || data.status === 'idle') return;
        if (data.event_type === 'complete') {
          setDrawingProgress({ ...data, phase: data.phase || 'complete' });
          setDrawingExtracting(false);
          if (data.status === 'done') {
            setCompletionResult({ status: 'done', data });
          } else if (data.status === 'error') {
            setCompletionResult({ status: 'error', data });
          }
        } else {
          setDrawingProgress(data);
        }
      } catch {}
    }, 5000);

    return () => {
      es.close();
      esRef.current = null;
      clearInterval(pollInterval);
    };
  }, [drawingExtracting, projectId]);

  const startTracking = useCallback(() => {
    setDrawingExtracting(true);
    setDrawingProgress(null);
    setCompletionResult(null);
  }, []);

  const reset = useCallback(() => {
    setDrawingProgress(null);
    setDrawingExtracting(false);
    setCompletionResult(null);
  }, []);

  return { drawingProgress, drawingExtracting, completionResult, startTracking, reset };
}

/**
 * Hook: Intake classification SSE progress.
 * Listens for intake_progress events while isActive is true.
 *
 * Returns:
 *  - intakeProgress: current progress object from SSE
 *  - reset(): clear progress state
 */
export function useIntakeProgress(projectId, isActive) {
  const [intakeProgress, setIntakeProgress] = useState(null);
  const esRef = useRef(null);

  useEffect(() => {
    if (!isActive) { setIntakeProgress(null); return; }
    const token = localStorage.getItem('fmiq_token');
    if (!token) return;

    const url = `${API_URL}/api/closeout/projects/${projectId}/events/stream?token=${encodeURIComponent(token)}`;
    let es;
    try { es = new EventSource(url); } catch { return; }
    esRef.current = es;

    es.addEventListener('intake_progress', (e) => {
      try { setIntakeProgress(JSON.parse(e.data)); } catch {}
    });
    es.onerror = () => {};

    return () => { es.close(); esRef.current = null; };
  }, [isActive, projectId]);

  const reset = useCallback(() => setIntakeProgress(null), []);

  return { intakeProgress, reset };
}
